import type { ReactNode } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AppWindow, ExternalLink } from 'lucide-react';
import { ehRotaInterna, urlEmbutivel, type LinkExterno } from '@malgoos/shared';
import { Cartao, Selo } from '../componentes/base';
import { Vazio } from '../componentes/estados';
import { SubtituloPagina } from '../componentes/subtitulo-pagina';
import { SeletorEmpresa } from '../nucleo/empresa/SeletorEmpresa';
import { ICONES_SUBSISTEMAS, useSubsistema, type ValorSubsistema } from '../nucleo/subsistemas';
import { useSessao } from '../nucleo/sessao';
import { caminhoExterno, useCelular } from './Externo';

/** Destino depois da escolha: o caminho pedido antes (rota interna, fora desta tela) ou o Início. */
export function destinoDepoisDaEscolha(voltar: string | null): string {
  return voltar && ehRotaInterna(voltar) && !voltar.startsWith('/subsistemas') ? voltar : '/';
}

const CARTAO = 'flex h-full w-full min-w-0 items-start gap-3 rounded-xl border-2 p-4 text-left transition-colors';

/**
 * D1 (sub-sistemas, L2): tela inicial com a empresa atual e os sub-sistemas que a pessoa usa nela (plano 3.5). A empresa
 * troca pelo seletor de sempre (no topo e aqui); trocar volta para esta tela com a empresa nova. O último usado nesta
 * empresa vem marcado. D3: os links externos abrem em nova aba; FUN 2026-10-14: o marcado "Abrir dentro do MalgoOS" abre a
 * página embutida (`/externo/<codigo>`), menos no celular, onde a nova aba é o padrão. Sem a configuração ligada, leva ao Início.
 */
export function EscolhaSubsistema() {
  const { t } = useTranslation();
  const s = useSessao();
  const sub = useSubsistema();
  const navegar = useNavigate();
  const [busca] = useSearchParams();
  // Review r58 robustez M1: celular também pelo toque (deitado passa de 768 px), não só pela largura.
  const desktop = !useCelular();
  if (!sub.ativo) return <Navigate to="/" replace />;
  const destino = destinoDepoisDaEscolha(busca.get('voltar'));
  const empresa = s.empresas?.todas ? t('empresa.todas') : s.empresas?.opcoes.find((o) => o.id === s.empresas?.atual)?.nome;
  return (
    <TelaEscolha
      sub={sub}
      empresa={empresa}
      seletor={<SeletorEmpresa className="min-w-0 max-w-72" />}
      desktop={desktop}
      aoEscolher={(codigo) => {
        sub.escolher(codigo);
        navegar(destino, { replace: true });
      }}
    />
  );
}

/** A tela em si (sem sessão nem rota: testada por `renderToString` em `tests/subsistemas-d1.test.ts`). */
export function TelaEscolha({
  sub,
  empresa,
  seletor,
  aoEscolher,
  desktop = true,
}: {
  sub: Pick<ValorSubsistema, 'opcoes' | 'links' | 'atual' | 'ultimo'>;
  empresa?: string;
  seletor?: ReactNode;
  aoEscolher: (codigo: string) => void;
  desktop?: boolean;
}) {
  const { t } = useTranslation();
  const marcado = sub.atual ?? sub.ultimo;
  return (
    <div className="flex flex-col gap-6" data-escolha-subsistema>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="kicker">{t('subsistemas.kicker')}</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight break-words">{t('subsistemas.titulo')}</h1>
          <SubtituloPagina caminho="/subsistemas" />
          {empresa && <p className="mt-1 text-sm font-medium text-tinta-2">{t('subsistemas.empresa', { nome: empresa })}</p>}
        </div>
        {seletor}
      </header>

      {sub.opcoes.length === 0 && sub.links.length === 0 ? (
        <Cartao>
          <Vazio texto={t('subsistemas.nenhum')} />
        </Cartao>
      ) : (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {sub.opcoes.map((codigo) => {
            const Icone = ICONES_SUBSISTEMAS[codigo];
            const eMarcado = codigo === marcado;
            return (
              <li key={codigo} className="min-w-0">
                <button
                  type="button"
                  data-subsistema={codigo}
                  aria-current={codigo === sub.atual ? 'true' : undefined}
                  onClick={() => aoEscolher(codigo)}
                  className={`${CARTAO} ${eMarcado ? 'border-acento-forte bg-acento-suave' : 'border-borda bg-sup-1 hover:bg-sup-2'}`}
                >
                  {Icone && (
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-sup-3 text-acento-forte">
                      <Icone className="size-5" aria-hidden />
                    </span>
                  )}
                  <span className="flex min-w-0 flex-col gap-1">
                    <span className="text-base font-semibold text-tinta-1">{t(`subsistemas.nomes.${codigo}`)}</span>
                    <span className="text-sm text-tinta-2">{t(`subsistemas.descricoes.${codigo}`)}</span>
                    {eMarcado && (
                      <span>
                        <Selo cor="acento">{t('subsistemas.ultimo')}</Selo>
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
          {sub.links.map((l) => (
            <li key={`link-${l.codigo}`} className="min-w-0">
              <CartaoLink link={l} desktop={desktop} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Cartão do link externo: nova aba, ou a página embutida quando marcado e permitido (no desktop). */
function CartaoLink({ link: l, desktop }: { link: LinkExterno; desktop: boolean }) {
  const { t } = useTranslation();
  const embutido = desktop && !!l.embutir && urlEmbutivel(l.url);
  const conteudo = (
    <>
      <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-sup-3 text-acento-forte">
        {embutido ? <AppWindow className="size-5" aria-hidden /> : <ExternalLink className="size-5" aria-hidden />}
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="text-base font-semibold break-words text-tinta-1">{l.nome}</span>
        <span className="text-sm break-all text-tinta-3">{new URL(l.url).hostname}</span>
        {!embutido && <span className="sr-only">{t('subsistemas.novaAba')}</span>}
      </span>
    </>
  );
  const classe = `${CARTAO} border-borda bg-sup-1 hover:bg-sup-2`;
  return embutido ? (
    <Link to={caminhoExterno(l.codigo)} data-link-externo={l.codigo} data-embutido="" className={classe}>
      {conteudo}
    </Link>
  ) : (
    <a href={l.url} target="_blank" rel="noopener noreferrer" data-link-externo={l.codigo} className={classe}>
      {conteudo}
    </a>
  );
}
